import { useEffect, useMemo } from "react";
import type { ApolloClient } from "@apollo/client";
import {
  clearImpersonateParam,
  getStoredImpersonateParam,
} from "@src/features/app/hooks/usePreserveImpersonateParam";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import {
  settleWithin,
  trackBeforeNavigating,
} from "@src/features/app/utils/track";
import {
  TrackEventDocument,
  TrackEventMutation,
  TrackEventMutationVariables,
} from "@src/features/app/operations.generated";
import { Ff, GraphqlOperations } from "@src/types.generated";
import {
  getImpersonatedUserFromUrl,
  IMPERSONATE_URL_PARAM,
  ImpersonatedUser,
  parseImpersonateParam,
  VIEW_AS_END_EVENT_FLAG,
} from "@src/lib/utils/impersonation";

interface StartImpersonatingOptions {
  redirectTo?: string;
}

// Where View as was started from, for adoption tracking.
export type ViewAsSource = "profile" | "table_row" | "admin_tools";

function formatImpersonateParam(user: ImpersonatedUser): string {
  return `${user.id}-${user.name}`;
}

/** Operations that bypass impersonation and always run as the real user. */
export const IMPERSONATION_EXEMPT_OPERATIONS: ReadonlySet<string> = new Set([
  GraphqlOperations.Query.SearchUsersForImpersonation,
  GraphqlOperations.Query.IsStaff,
  // Logging out ends the viewer's own session.
  GraphqlOperations.Mutation.Logout,
]);

// Where the viewer started, so Exit returns there (the person's profile).
// Per tab, like the preserved ?impersonate= param.
const RETURN_TO_STORAGE_KEY = "impersonation-return-to";

// One start per page: a double-click or a second row while the first start
// is in flight sends no second "started_view_as" and no second navigation.
// The page is left right after, so it never resets.
let starting: Promise<void> | null = null;

/**
 * Starts the view. Resolves only if the page is still here shortly after it
 * began leaving (a beforeunload "Stay"), with the start latch reset so the
 * viewer can try again or back out.
 */
export function startImpersonating(
  user: ImpersonatedUser,
  source: ViewAsSource,
  options?: StartImpersonatingOptions,
): Promise<void> {
  starting ??= start(user, source, options).finally(() => {
    starting = null;
  });
  return starting;
}

/** Reloads the page; replaceable in tests (jsdom can't reload). */
export const viewAsNavigation = {
  reload: (): void => window.location.reload(),
};

/** How long a page may stay after a navigation began before it's "Stay". */
export const viewAsTiming = { stayDetectMs: 2000 };

/**
 * Resolves if the page is still shown `viewAsTiming.stayDetectMs` after a
 * navigation began (a beforeunload "Stay" cancelled it); never resolves once
 * the page is hidden.
 */
function whenStillHereAfterLeaving(): Promise<void> {
  return new Promise((resolve) => {
    const onHide = () => clearTimeout(timer);
    const timer = setTimeout(() => {
      window.removeEventListener("pagehide", onHide);
      resolve();
    }, viewAsTiming.stayDetectMs);
    window.addEventListener("pagehide", onHide, { once: true });
  });
}

async function start(
  user: ImpersonatedUser,
  source: ViewAsSource,
  options?: StartImpersonatingOptions,
): Promise<void> {
  // Sent as the viewer: the ?impersonate= param (and so the
  // X-Impersonate-User-Id header and the write guard, which both read it)
  // only exists after the navigation below.
  await trackBeforeNavigating("started_view_as", {
    targetUserId: user.id,
    source,
  });
  const here = new URL(window.location.href);
  here.searchParams.delete(IMPERSONATE_URL_PARAM);
  try {
    // Switching people keeps where the session first started.
    if (!getImpersonatedUserFromUrl()) {
      window.sessionStorage.setItem(
        RETURN_TO_STORAGE_KEY,
        `${here.pathname}${here.search}`,
      );
    }
  } catch {
    // Storage off: Exit falls back to the viewer's home.
  }
  const target = options?.redirectTo ?? window.location.pathname;
  const url = new URL(target, window.location.origin);
  if (!options?.redirectTo) {
    url.search = window.location.search;
  }
  url.searchParams.set(IMPERSONATE_URL_PARAM, formatImpersonateParam(user));
  // Replace, so Back from the view never lands on the page the view was
  // started from (as the viewer, with the person's state in memory).
  window.location.replace(url.toString());
  await whenStillHereAfterLeaving();
}

function readReturnTo(): string {
  try {
    const returnTo = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    // Same-origin paths only.
    if (returnTo?.startsWith("/") && !returnTo.startsWith("//")) {
      return returnTo;
    }
  } catch {
    // Storage off: fall back to home.
  }
  return "/";
}

function clearReturnTo(): void {
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // Storage off: nothing kept.
  }
}

/** Why a view ended, sent with "ended_view_as". */
export type ViewAsEndReason =
  // The banner's Exit.
  | "exit"
  // The server refused to act as the person (403 impersonation_forbidden).
  | "forbidden"
  // Logged out while viewing.
  | "logout"
  // A full load without ?impersonate= while the tab still kept it (Back to
  // a page from before the view, a typed URL).
  | "navigated_away";

// The guarded app client, registered by the Apollo module; read at call time
// so this module needn't import it (it imports this one).
let endEventClient: ApolloClient<unknown> | null = null;

export function setViewAsEndEventClient<T>(client: ApolloClient<T>): void {
  endEventClient = client as unknown as ApolloClient<unknown>;
}

/**
 * Sends a TrackEvent as the viewer and waits for it (at most `timeoutMs`),
 * even while still viewing. It is sent straight on the Apollo client, not
 * through the tracking context, so it also works when that isn't mounted (a
 * refused page load). Only this flagged TrackEvent skips the
 * X-Impersonate-User-Id header and the write guards (see
 * VIEW_AS_END_EVENT_FLAG), so nothing else leaves as the viewer.
 */
export async function trackAsViewer(
  label: string,
  payload: Record<string, unknown>,
  timeoutMs = 1000,
): Promise<void> {
  const client = endEventClient;
  if (!client) return;
  await settleWithin(
    () =>
      client.mutate<TrackEventMutation, TrackEventMutationVariables>({
        mutation: TrackEventDocument,
        variables: { label, payload },
        context: {
          [VIEW_AS_END_EVENT_FLAG]: true,
          fetchOptions: { [VIEW_AS_END_EVENT_FLAG]: true },
        },
      }),
    timeoutMs,
  );
}

/** Sends "ended_view_as"; every way out of a view goes through here. */
export function sendEndedViewAs(
  targetUserId: number,
  reason: ViewAsEndReason,
): Promise<void> {
  return trackAsViewer("ended_view_as", { targetUserId, reason });
}

// Set by Exit just before it leaves, so the next load (whose URL has no
// ?impersonate= while the tab still keeps one) isn't reported as navigated
// away. One-shot, and only honoured for a short while: a "Stay" leaves it
// behind, and a much later load is a real navigation away.
const EXIT_MARKER_STORAGE_KEY = "impersonation-exiting";
const EXIT_MARKER_TTL_MS = 30_000;

function markExiting(): void {
  try {
    window.sessionStorage.setItem(EXIT_MARKER_STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage off: nothing is kept to report either.
  }
}

/** Whether this load follows an Exit; consumes the marker. */
function takeExitMarker(): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(EXIT_MARKER_STORAGE_KEY));
    window.sessionStorage.removeItem(EXIT_MARKER_STORAGE_KEY);
    return at > 0 && Date.now() - at < EXIT_MARKER_TTL_MS;
  } catch {
    return false;
  }
}

let stopping = false;

/**
 * Leaves the view and reloads as the viewer where they started. The end
 * event goes out while the page is still viewing (so every other request
 * stays impersonated and guarded until the page is gone); then the page is
 * replaced. The tab's kept view is left for the next load to drop (its URL
 * wins), so a "Stay" leaves the viewer still viewing, read-only, and Exit
 * works again.
 */
export async function stopImpersonating(
  reason: Extract<ViewAsEndReason, "exit" | "forbidden"> = "exit",
): Promise<void> {
  // Several refused requests can arrive at once; leave once.
  if (stopping) return;
  stopping = true;
  const viewed = getImpersonatedUserFromUrl();
  if (viewed) {
    await sendEndedViewAs(viewed.id, reason);
  }
  markExiting();
  // Replace, so Back doesn't return to the view.
  window.location.replace(readReturnTo());
  await whenStillHereAfterLeaving();
  // After a refusal the page stays latched: polling would keep getting 403s
  // and re-trigger the exit every few seconds.
  if (reason !== "forbidden") stopping = false;
}

/**
 * A page shown without ?impersonate= while this tab still keeps one: after an
 * Exit (its marker), just tidy up; otherwise the view was left some other way
 * (Back, a typed URL, a page restored from the back/forward cache), so send
 * the end event (the headers are already the viewer's). Then drop the kept
 * view and return path.
 */
function endViewIfNavigatedAway(): void {
  if (getImpersonatedUserFromUrl()) return;
  const exited = takeExitMarker();
  const stored = getStoredImpersonateParam();
  if (!stored) return;
  const viewed = parseImpersonateParam(stored);
  if (viewed && !exited) void sendEndedViewAs(viewed.id, "navigated_away");
  clearImpersonateParam();
  clearReturnTo();
}

/**
 * Runs the navigated-away check on every load and on back/forward cache
 * restores (which mount nothing). Call before usePreserveImpersonateParam.
 */
export function useEndViewAsWhenNavigatedAway(): void {
  useEffect(() => {
    endViewIfNavigatedAway();
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (getImpersonatedUserFromUrl() && !getStoredImpersonateParam()) {
        // A viewing page restored after the tab's view was dropped: the
        // headers and guards (URL) and the kept view would disagree, so load
        // it fresh.
        viewAsNavigation.reload();
        return;
      }
      endViewIfNavigatedAway();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
}

export function useCanImpersonate(): boolean {
  const impersonationEnabled = useFeatureFlag(Ff.Impersonation);
  const { isStaff } = useIsStaff();
  const { userActionPermissions } = useActionPermissions();
  // While viewing as someone, permissions resolve for that person, so the
  // entry shows as it would for them; starting another view is refused at
  // the entry (useViewAs) instead.
  return (
    !!impersonationEnabled &&
    (isStaff || !!userActionPermissions?.impersonateUsers)
  );
}

export function useImpersonation(): {
  impersonatedUser: { id: number; name: string; userType: string } | null;
  isImpersonating: boolean;
} {
  // Intentionally mount-only: start/stop both trigger full page reloads,
  // so the URL is always fresh on mount.
  const impersonatedUser = useMemo(() => getImpersonatedUserFromUrl(), []);

  const isImpersonating = impersonatedUser !== null;

  return { impersonatedUser, isImpersonating };
}
